'use client';
import { Warning } from '@mui/icons-material';
import { Box, Tab, Tabs } from '@mui/material';
import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';
import ProgressTab from '@/components/course/progress-tab';
import ResultsTab from '@/components/course/results-tab';
import CurriculumTab from '@/components/curriculum/curriculum-tab';
import FlashcardDecksTab from '@/components/flashcard/flashcard-decks-tab';
import AppLayout from '@/components/layout/app-layout';
import LoadingIndicator from '@/components/shared/loading-indicator';
import LoginPrompt from '@/components/shared/login-prompt';
import { CourseTabList } from '@/constants/menu';
import { useAuth, useCourses } from '@/providers';
import { useRoute } from '@/utils/client/route';

function TabPanel(props: { children?: ReactNode; tabId: string; value: string }) {
  const { children, value, tabId, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== tabId}
      id={`tabpanel-${tabId}`}
      aria-labelledby={`tab-${tabId}`}
      {...other}>
      {value === tabId && (
        <Box
          sx={{
            width: '100%',
            px: { xs: 1, sm: 1.5, md: 3 },
            mb: 8,
            overflowX: 'auto',
          }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(tabId: string) {
  return {
    id: `tab-${tabId}`,
    'aria-controls': `tabpanel-${tabId}`,
    value: tabId,
  };
}

export default function Page() {
  const { goToRoute } = useRoute();
  const { selectedCourse } = useCourses();
  const { isLoggedIn } = useAuth();
  const { courseSlug, tabId: tabIdParam } = useParams() as {
    courseSlug: string;
    tabId: string;
  };

  const { courseStatus, isLoading } = useCourses();

  if (!selectedCourse || isLoading) {
    return (
      <AppLayout>
        <LoadingIndicator label="Kursinhalte werden geladen..." />
      </AppLayout>
    );
  }

  const isTest = selectedCourse.type === 'Test';
  const hasFlashcards = selectedCourse.flashcardDecks?.length > 0;

  const availableTabs = [
    CourseTabList.inhalte,
    CourseTabList.fortschritt,
    ...(isTest ? [CourseTabList.ergebnisse] : []),
    ...(!isTest && hasFlashcards ? [CourseTabList.karteikarten] : []),
  ];

  const activeTabId = availableTabs.includes(tabIdParam as CourseTabList)
    ? tabIdParam
    : CourseTabList.inhalte;

  const handleNewTabSelected = (newTabId: string) => {
    goToRoute('myCourseTabs', { courseSlug, tabId: newTabId });
  };

  return (
    <AppLayout>
      <Tabs
        value={activeTabId}
        onChange={(_event, value) => handleNewTabSelected(value)}
        aria-label="cours tabs"
        centered
        sx={{ mb: 2 }}>
        <Tab label="Inhalte" {...a11yProps(CourseTabList.inhalte)} />
        <Tab
          label={
            <Box sx={{ display: 'flex', gap: 1 }}>
              Fortschritt
              {isLoggedIn && !courseStatus?.plannedToFinishAt && <Warning color="warning" />}
            </Box>
          }
          {...a11yProps(CourseTabList.fortschritt)}
        />
        {isTest && <Tab label="Ergebnisse" {...a11yProps(CourseTabList.ergebnisse)} />}
        {!isTest && hasFlashcards && (
          <Tab label="Karteikarten" {...a11yProps(CourseTabList.karteikarten)} />
        )}
      </Tabs>
      <TabPanel value={activeTabId} tabId={CourseTabList.inhalte}>
        <CurriculumTab />
      </TabPanel>
      <TabPanel value={activeTabId} tabId={CourseTabList.fortschritt}>
        {!isLoggedIn && (
          <LoginPrompt>Bitte registriere Dich für Deinen Lernplan und Fortschritt.</LoginPrompt>
        )}
        {isLoggedIn && <ProgressTab />}
      </TabPanel>
      {isTest && (
        <TabPanel value={activeTabId} tabId={CourseTabList.ergebnisse}>
          {!isLoggedIn && (
            <LoginPrompt>Bitte melde Dich an, um Deine Testergebnisse einzusehen.</LoginPrompt>
          )}
          {isLoggedIn && <ResultsTab />}
        </TabPanel>
      )}
      {!isTest && hasFlashcards && (
        <TabPanel value={activeTabId} tabId={CourseTabList.karteikarten}>
          {!isLoggedIn && (
            <LoginPrompt>
              Bitte melde Dich an, um Karteikarten nutzen zu können und Deinen Lernerfolg zu
              steigern.
            </LoginPrompt>
          )}
          {isLoggedIn && <FlashcardDecksTab />}
        </TabPanel>
      )}
    </AppLayout>
  );
}
